import * as React from 'react';

import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
  DragStartEvent,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
  ColumnResizeMode,
  ColumnOrderState,
  Column,
  ColumnResizeDirection,
  Header,
} from '@tanstack/react-table';
import { useCopyToClipboard, useDebounce } from '@uidotdev/usehooks';
import groupBy from 'lodash/groupBy';
import isObject from 'lodash/isObject';
import { LucideFrown } from 'lucide-react';
import useResizeObserver from 'use-resize-observer';

import { useCellRangeSelection } from '../../../../hooks/useCellRangeSelection';
import { useTranslations } from '../../../../i18n/useTranslations';
import { cn } from '../../../../lib/stylingUtils';
import { Loading } from '../../feedback/Loading';
import { Empty } from '../../feedback/MaybeEmpty';
import type { EmptyProps } from '../../feedback/MaybeEmpty';
import { Checkbox } from '../../forms/Checkbox';

import { HeaderIcons } from './Icons';
import { TableCell } from './TableCell';
import {
  type DataGridColumn,
  GroupRow,
  TableHeader,
  type TableHeaderProps,
} from './TableHeader';
import { useColumnOrder } from './useColumnOrder';
import { useSelectionState } from './useSelectionState';

/** Represents a specific cell position in the grid */
export interface CellPosition {
  /** The unique identifier of the row */
  rowId: string;
  /** The unique identifier of the column */
  columnId: string;
}

/** Represents a range of selected cells from start to end position */
export interface CellRange {
  /** The starting cell of the selection range */
  startCell: CellPosition;
  /** The ending cell of the selection range */
  endCell: CellPosition;
}

// Enhanced selection types following React Aria patterns

/** The type of selection allowed in the data grid */
export type DataGridSelectionMode = 'none' | 'single' | 'multiple';

/** Represents the current selection state - either all rows or a set of specific row keys */
export type DataGridSelection = 'all' | Set<React.Key>;

/** How selection should behave when interacting with rows */
export type DataGridSelectionBehavior = 'toggle' | 'replace';

export type DataGridRow<T> = T | GroupRow<T>;

const isGroupRow = <T,>(row: DataGridRow<T>): row is GroupRow<T> =>
  isObject(row) && 'groupKey' in row;

export interface DataGridProps<T> {
  /**
   * Whether the grid is contained within a container
   * @default false
   */
  isContained?: boolean;

  /** Column definitions for the data grid */
  columns: DataGridColumn<T>[];

  /** Array of data objects to display in the grid */
  dataSource: T[];

  /**
   * Whether to show the table header
   * @default true
   */
  showHeader?: boolean;

  /**
   * Whether the header should stick to the top when scrolling
   * @default true
   */
  stickyHeader?: boolean;

  /**
   * Fixed height for the scrollable area. Can be a CSS value or number (px)
   */
  scrollHeight?: string | number;

  /**
   * Custom loading indicator component
   * @default Loading component with lg size
   */
  loadingIndicator?: React.ReactNode;

  /**
   * Whether the table should expand to fill available width
   * @default false
   */
  fullWidth?: boolean;

  /** Props to customize the empty state when no data is available */
  emptyPanelProps?: EmptyProps;

  /** Props to customize the error state when hasError is true */
  errorPanelProps?: EmptyProps;

  /** Function to group rows by a common value. Returns the group key for each row */
  groupBy?: (row: T) => React.Key;

  /**
   * The type of selection that is allowed in the collection.
   * @default 'none'
   */
  selectionMode?: DataGridSelectionMode;

  /**
   * The currently selected keys in the collection (controlled).
   * Use this when you want to control the selection state externally.
   */
  selectedKeys?: DataGridSelection;

  /**
   * The initial selection of keys in the collection (uncontrolled).
   * Use this when you want the component to manage its own selection state.
   */
  defaultSelectedKeys?: DataGridSelection;

  /**
   * Handler that is called when the selection changes.
   * @param keys - The new selection
   */
  onSelectionChange?: (keys: DataGridSelection) => void;

  /**
   * How selection should be displayed.
   * - "toggle": Selected items have a selected appearance
   * - "replace": Selection has a highlighted appearance, items are replaced on selection
   * @default 'toggle'
   */
  selectionBehavior?: DataGridSelectionBehavior;

  /**
   * A set of keys for rows that are disabled and cannot be selected.
   */
  disabledKeys?: Set<React.Key>;

  /**
   * Whether the collection allows empty selection.
   * @default false
   */
  disallowEmptySelection?: boolean;

  /** Called when a column is resized. Receives the column ID and new width */
  onColumnResize?: (columnId: string, width: number) => void;

  /** Called when columns are reordered. Receives the new column order array */
  onColumnReorder?: (newOrder: string[]) => void;

  /**
   * Called when a column header is clicked for sorting
   * @param columnId - The ID of the column being sorted
   * @param direction - The sort direction, or null to clear sorting
   */
  onColumnSort?: (
    columnId: string,
    direction: 'ascending' | 'descending' | null,
  ) => void;

  /** Object controlling which columns are visible. Key is column ID, value is visibility */
  columnVisibilityState?: Record<string, boolean>;

  /** Called when cell range selection changes. Used for Excel-like cell selection */
  onRangeSelectionChange?: (range: CellRange | null) => void;

  /**
   * Unique key to persist column order in localStorage.
   * When provided, column order will be saved and restored across page reloads.
   * Format: "<module>:<view>" (e.g., "trade:loads-list")
   */
  persistColumnOrderKey?: string;

  /**
   * Property name to use as the unique row identifier
   * @default 'id'
   */
  rowKey?: keyof T;

  /** Additional CSS class names to apply to the grid container */
  className?: string;

  /**
   * Whether the grid is in a loading state. Shows loading indicator when true
   * @default false
   */
  isLoading?: boolean;

  /**
   * Whether the grid has an error. Shows error state when true
   * @default false
   */
  hasError?: boolean;
}

interface DataGridColumnSorting {
  id: string;
  direction: 'ascending' | 'descending';
}

export { type DataGridColumn, HeaderIcons };

const DEFAULT_COLUMN_WIDTH = 150;
const SELECTABLE_COLUMN_WIDTH = 30;

export const DataGrid = <T,>({
  className,
  columns: columnsProp,
  dataSource: rawDataSource,
  showHeader = true,
  stickyHeader = true,
  scrollHeight,
  loadingIndicator = <Loading isLoading isBoxed />,
  fullWidth = false,
  groupBy: groupByFn,
  rowKey = 'id' as keyof T,
  isLoading,
  hasError,
  emptyPanelProps,
  errorPanelProps,
  isContained = false,

  // Selection props
  selectionMode = 'none',
  selectedKeys,
  defaultSelectedKeys,
  onSelectionChange,
  selectionBehavior: _selectionBehavior = 'toggle',
  disabledKeys: disabledKeysProp,
  disallowEmptySelection = false,

  // Column callbacks
  onColumnResize,
  onColumnReorder,
  onColumnSort,
  columnVisibilityState,

  // Range selection callback
  onRangeSelectionChange,

  // Column order persistence
  persistColumnOrderKey,
}: DataGridProps<T>) => {
  const t = useTranslations();

  const dataSource = React.useMemo(
    () =>
      groupByFn
        ? Object.values(groupBy(rawDataSource, groupByFn)).flatMap(
            (entries) => [
              {
                groupKey: groupByFn(entries[0]),
                children: entries,
              } as GroupRow<T>,
              ...entries,
            ],
          )
        : rawDataSource,
    [groupByFn, rawDataSource],
  );

  const disabledKeys = React.useMemo(
    () =>
      new Set([
        ...(disabledKeysProp ? Array.from(disabledKeysProp) : []),
        ...dataSource.filter(isGroupRow).map((it) => String(it.groupKey)),
      ]),
    [dataSource, disabledKeysProp],
  );

  // Use the selection state hook
  const {
    rowSelectionState,
    handleSelectionChange,
    getRowCanSelect,
    currentSelection,
  } = useSelectionState({
    selectedKeys,
    defaultSelectedKeys,
    onSelectionChange,
    selectionMode,
    rowKey: (x) =>
      isGroupRow(x) ? String(x.groupKey) : String((x as T)[rowKey]),
    dataSource,
    disabledKeys,
    disallowEmptySelection,
  });

  const stickyHeaders = React.useMemo(
    () => stickyHeader || columnsProp.some((it) => it.pinned),
    [stickyHeader, columnsProp],
  );

  const domainColumns = React.useMemo(() => {
    if (selectionMode === 'none') return columnsProp;

    return [
      {
        key: 'selection',
        header: undefined,
        pinned: stickyHeaders ? 'left' : 'none',
        width: SELECTABLE_COLUMN_WIDTH,
        cell: () => undefined,
      },
      ...columnsProp,
    ] as DataGridColumn<T>[];
  }, [selectionMode, columnsProp, stickyHeaders]);

  const [, setActiveHeader] = React.useState<Header<T, unknown> | undefined>();

  const defaultColumnOrder = React.useMemo(
    () => domainColumns.map((col) => col.key),
    [domainColumns],
  );

  const {
    columnOrder: persistedColumnOrder,
    setColumnOrder: setPersistedColumnOrder,
  } = useColumnOrder(persistColumnOrderKey, defaultColumnOrder);

  const [localColumnOrder, setLocalColumnOrder] =
    React.useState<ColumnOrderState>(defaultColumnOrder);

  const columnOrder = persistColumnOrderKey
    ? persistedColumnOrder
    : localColumnOrder;

  const setColumnOrder = React.useCallback(
    (
      updaterOrValue:
        | ColumnOrderState
        | ((prev: ColumnOrderState) => ColumnOrderState),
    ) => {
      if (persistColumnOrderKey) {
        setPersistedColumnOrder(updaterOrValue);
      } else {
        setLocalColumnOrder(updaterOrValue);
      }
    },
    [persistColumnOrderKey, setPersistedColumnOrder],
  );
  const [columnResizeMode] = React.useState<ColumnResizeMode>('onChange');
  const [columnResizeDirection] = React.useState<ColumnResizeDirection>('ltr');
  const [columnSorting, setColumnSorting] =
    React.useState<DataGridColumnSorting | null>(null);
  const [, copy] = useCopyToClipboard();
  const [tableWidth, setTableWidth] = React.useState<number | null>(null);
  const [hasScrolled, setHasScrolled] = React.useState(false);
  const [maxHLScrolled, setMaxHLScrolled] = React.useState(true);
  const [maxHRScrolled, setMaxHRScrolled] = React.useState(false);

  const rootRef = React.useRef<HTMLDivElement>(null);
  const tableRef = React.useRef<HTMLTableElement>(null);

  const { width: rootWidth } = useResizeObserver<HTMLDivElement>({
    ref: rootRef as never,
    box: 'border-box',
  });

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  const debouncedRootWidth = useDebounce(rootWidth, 100);

  // Apply root width to table width if fullWidth is true
  React.useEffect(() => {
    if (fullWidth && debouncedRootWidth && tableWidth !== debouncedRootWidth) {
      setTableWidth(debouncedRootWidth - 1);
    }
  }, [fullWidth, tableWidth, debouncedRootWidth]);

  // Handle scroll events
  React.useLayoutEffect(() => {
    const handleScroll = () => {
      if (rootRef.current) {
        setHasScrolled(rootRef.current.scrollTop > 0);
        setMaxHLScrolled(rootRef.current.scrollLeft === 0);
        setMaxHRScrolled(
          rootRef.current.scrollLeft + rootRef.current.clientWidth >=
            rootRef.current.scrollWidth,
        );
      }
    };

    const scrollContainer = rootRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    } else {
      return undefined;
    }
  }, [rootWidth]);

  // Set header height CSS variable for shadow positioning
  React.useEffect(() => {
    if (!rootRef.current || !stickyHeader || !showHeader) return;

    const observer = new ResizeObserver(() => {
      const headerElement = rootRef.current?.querySelector('thead');
      if (headerElement) {
        const headerHeight = headerElement.getBoundingClientRect().height;
        rootRef.current?.style.setProperty(
          '--header-height',
          `${headerHeight}px`,
        );
      }
    });

    const headerElement = rootRef.current.querySelector('thead');
    if (headerElement) {
      observer.observe(headerElement);
    }

    return () => {
      observer.disconnect();
    };
  }, [stickyHeader, showHeader]);

  const columns = React.useMemo<ColumnDef<DataGridRow<T>>[]>(() => {
    const columnsWithGrowth = domainColumns.filter(
      (col) => col.grow !== undefined,
    );
    const hasGrowthColumns = columnsWithGrowth.length > 0;
    let columnWidths: (number | undefined)[] = domainColumns.map(
      (col) => col.width,
    );

    if (!hasGrowthColumns && tableWidth && fullWidth) {
      // Has no growth columns, distribute space evenly
      const totalWeight = domainColumns.reduce(
        (sum, col) => sum + (col.width ?? DEFAULT_COLUMN_WIDTH),
        0,
      );
      columnWidths = domainColumns.map((col) =>
        Math.max(
          Math.floor(
            (tableWidth * (col.width ?? DEFAULT_COLUMN_WIDTH)) / totalWeight,
          ),
          col.width ?? DEFAULT_COLUMN_WIDTH,
        ),
      );
    } else if (tableWidth && fullWidth) {
      // Has growth columns, maintain explicit widths and distribute remaining space
      // into growth columns

      const nonGrowthWidth = domainColumns
        .filter((col) => col.grow === undefined)
        .reduce((sum, col) => sum + (col.width || DEFAULT_COLUMN_WIDTH), 0);
      const remainingWidth = Math.max(0, tableWidth - nonGrowthWidth);
      const totalGrowth = columnsWithGrowth.reduce(
        (sum, col) => sum + (col.grow || 0),
        0,
      );

      columnWidths = domainColumns.map((col) =>
        col.grow
          ? Math.max(
              col.width ?? DEFAULT_COLUMN_WIDTH,
              Math.floor((remainingWidth * col.grow) / totalGrowth),
            )
          : (col.width ?? DEFAULT_COLUMN_WIDTH),
      );
    }

    return domainColumns.map((col, i) => ({
      id: col.key,
      accessorFn: (row: T) => row[col.key as keyof T],
      meta: col,
      header: ({ table }) => {
        if (col.key === 'selection') {
          const isSelected =
            currentSelection === 'all' ||
            (currentSelection.size === rawDataSource.length &&
              currentSelection.size > 0);

          const isIndeterminate =
            currentSelection !== 'all' &&
            currentSelection.size > 0 &&
            currentSelection.size < rawDataSource.length;

          return (
            <Checkbox
              aria-label={t.components.dataGrid.selectAll}
              isSelected={isSelected}
              isIndeterminate={isIndeterminate}
              onChange={(selection) => table.toggleAllRowsSelected(selection)}
            />
          );
        }

        const Icon = col.icon;
        return (
          <div className="flex flex-row items-center gap-1">
            {Icon && <Icon />}
            <div>{col.header}</div>
          </div>
        );
      },
      cell: ({ row }) => {
        if (col.key === 'selection') {
          const isSelected = isGroupRow(row.original)
            ? row.original.children?.every(
                (child) =>
                  selectedKeys === 'all' ||
                  selectedKeys?.has(String(child[rowKey])),
              )
            : row.getIsSelected();

          const isIndeterminate = isGroupRow(row.original)
            ? !isSelected &&
              row.original.children?.some(
                (child) =>
                  selectedKeys !== 'all' &&
                  selectedKeys?.has(String(child[rowKey])),
              )
            : row.getIsSomeSelected();

          return (
            <Checkbox
              aria-label={t.components.dataGrid.selectRow}
              isSelected={isSelected}
              disabled={isGroupRow(row.original) ? false : !row.getCanSelect()}
              isIndeterminate={isIndeterminate}
              onChange={
                isGroupRow(row.original)
                  ? (isChecked) => {
                      const newSelection = new Set(selectedKeys);

                      if (!isGroupRow(row.original)) return;

                      if (isChecked) {
                        row.original.children?.forEach((child) => {
                          newSelection.add(String(child[rowKey]));
                        });
                      } else {
                        row.original.children.forEach((child) => {
                          newSelection.delete(String(child[rowKey]));
                        });
                      }

                      onSelectionChange?.(newSelection);
                    }
                  : row.getToggleSelectedHandler()
              }
            />
          );
        }

        return isGroupRow(row.original)
          ? col.groupCell?.(row.original)
          : col.cell({
              row: row.original,
              column: col,
            });
      },
      size: columnWidths[i],
      enableResizing: col.resizable === true,
      enablePinning: !!col.pinned,
      enableSorting: col.sortable === true,
    }));
  }, [
    domainColumns,
    tableWidth,
    fullWidth,
    currentSelection,
    rawDataSource.length,
    t.components.dataGrid.selectAll,
    t.components.dataGrid.selectRow,
    selectedKeys,
    rowKey,
    onSelectionChange,
  ]);

  const table = useReactTable({
    data: dataSource,
    columns,
    state: {
      columnOrder,
      rowSelection: rowSelectionState,
      columnVisibility: columnVisibilityState,
    },
    enableRowSelection: selectionMode !== 'none' ? getRowCanSelect : false,
    enableMultiRowSelection: selectionMode === 'multiple',
    enableColumnResizing: true,
    enableColumnPinning: true,
    columnResizeMode,
    columnResizeDirection,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: handleSelectionChange,
    onColumnOrderChange: setColumnOrder,
    getRowId: (row) =>
      isGroupRow(row) ? String(row.groupKey) : String((row as T)[rowKey]),
  });

  const {
    selectedCell,
    selection: selectedRange,
    getCellRef,
    isCellInRange,
    handleClick,
    handleKeyDown,
    handleMouseDown,
    handleMouseEnter,
    setSelection,
    setSelectedCell,
  } = useCellRangeSelection(
    table.getRowModel().rows,
    table.getVisibleFlatColumns(),
    columnOrder,
  );

  const selectionOverlayRef = React.useRef<HTMLDivElement>(null);
  const selectedCellOverlayRef = React.useRef<HTMLDivElement>(null);

  const handleColumnSorting = React.useCallback(
    (columnId: string) => {
      if (columnSorting?.id === columnId) {
        if (columnSorting.direction === 'ascending') {
          // Cycle from ascending to descending
          const newSort = { id: columnId, direction: 'descending' as const };
          setColumnSorting(newSort);
          onColumnSort?.(columnId, 'descending');
        } else {
          // Cycle from descending to no sort
          setColumnSorting(null);
          onColumnSort?.(columnId, null);
        }
      } else {
        // No sort or different column sort, so set to ascending
        const newSort = { id: columnId, direction: 'ascending' as const };
        setColumnSorting(newSort);
        onColumnSort?.(columnId, 'ascending');
      }
    },
    [onColumnSort, columnSorting],
  );

  const handleDragStart = React.useCallback(
    (event: DragStartEvent) => {
      const { active } = event;

      const draggingHeader = table
        .getFlatHeaders()
        .find((h) => h.id === active.id);

      setActiveHeader(draggingHeader as Header<T, unknown>);
      setSelectedCell(null);
      setSelection(null);
    },
    [setSelectedCell, setSelection, setActiveHeader, table],
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      setActiveHeader(undefined);

      if (active && over && active.id !== over.id) {
        setColumnOrder((columnOrder) => {
          const oldIndex = columnOrder.indexOf(active.id as string);
          const newIndex = columnOrder.indexOf(over.id as string);
          const newOrder = arrayMove(columnOrder, oldIndex, newIndex);

          onColumnReorder?.(newOrder);

          // Clears selection to avoid having an orphan selection
          // overlay
          setSelection(null);

          return newOrder;
        });
      }
    },
    [onColumnReorder, setSelection, setColumnOrder],
  );

  const handleCopy = React.useCallback(
    async (e: React.ClipboardEvent<Element>) => {
      e.preventDefault();
      if (!selectedRange) return;

      const { start, end } = selectedRange;
      const rows = table.getRowModel().rows;
      const columns = columnOrder.map((id) =>
        table.getAllColumns().find((c) => c.id === id),
      ) as Column<T, unknown>[];

      const startRowIndex = rows.findIndex((row) => row.id === start.rowId);
      const endRowIndex = rows.findIndex((row) => row.id === end.rowId);
      const startColIndex = columns.findIndex(
        (col) => col.id === start.columnId,
      );
      const endColIndex = columns.findIndex((col) => col.id === end.columnId);

      const values = rows
        .slice(
          Math.min(startRowIndex, endRowIndex),
          Math.max(startRowIndex, endRowIndex) + 1,
        )
        .map((row) =>
          columns
            .slice(
              Math.min(startColIndex, endColIndex),
              Math.max(startColIndex, endColIndex) + 1,
            )
            .map((column) => {
              const cellEl = getCellRef(row.id, column.id);
              return cellEl.current?.innerText ?? '';
            })
            .join('\t'),
        )
        .join('\n');

      await copy(values);
    },
    [selectedRange, table, columnOrder, getCellRef, copy],
  );

  const handleTableEvent = (
    e:
      | React.MouseEvent<HTMLTableSectionElement>
      | React.KeyboardEvent<HTMLTableSectionElement>,
  ) => {
    const cell = (e.target as HTMLElement).closest('td');
    if (!cell) return;

    const rowId = cell.getAttribute('data-row-id');
    const columnId = cell.getAttribute('data-column-id');
    if (!rowId || !columnId) return;

    switch (e.type) {
      case 'click':
        handleClick(rowId, columnId);
        break;
      case 'keydown':
        handleKeyDown(
          e as React.KeyboardEvent<HTMLTableSectionElement>,
          rowId,
          columnId,
        );
        break;
      case 'mousedown':
        handleMouseDown(rowId, columnId);
        break;
      case 'mouseenter':
        handleMouseEnter(rowId, columnId);
        break;
      case 'mousemove':
        handleMouseEnter(rowId, columnId);
        break;
    }
  };

  // Clear selection on data source change
  React.useEffect(() => {
    if (dataSource) {
      setSelection(null);
      setSelectedCell(null);
    }
  }, [dataSource, setSelection, setSelectedCell]);

  // Update selection overlay position
  React.useEffect(() => {
    if (!selectedRange || !selectionOverlayRef.current) return;

    const startCell = getCellRef(
      selectedRange.start.rowId,
      selectedRange.start.columnId,
    );
    const endCell = getCellRef(
      selectedRange.end.rowId,
      selectedRange.end.columnId,
    );

    if (!startCell.current || !endCell.current) return;

    const startRect = startCell.current.getBoundingClientRect();
    const endRect = endCell.current.getBoundingClientRect();
    const tableRect =
      selectionOverlayRef.current.parentElement?.getBoundingClientRect();

    // Get the scrolling position of the parent element
    const scrollLeft =
      selectionOverlayRef.current.parentElement?.scrollLeft || 0;
    const scrollTop = selectionOverlayRef.current.parentElement?.scrollTop || 0;

    const left =
      Math.min(startRect.left, endRect.left) -
      (tableRect?.left ?? Number.POSITIVE_INFINITY) -
      1;
    const top =
      Math.min(startRect.top, endRect.top) -
      (tableRect?.top ?? Number.POSITIVE_INFINITY) -
      1;
    const right =
      Math.max(startRect.right, endRect.right) - (tableRect?.left ?? 0);
    const bottom =
      Math.max(startRect.bottom, endRect.bottom) - (tableRect?.top ?? 0);

    selectionOverlayRef.current.style.left = `${left + scrollLeft}px`;
    selectionOverlayRef.current.style.top = `${top + scrollTop}px`;
    selectionOverlayRef.current.style.width = `${right - left}px`;
    selectionOverlayRef.current.style.height = `${bottom - top}px`;
  }, [selectedRange, getCellRef]);

  // Update selected cell overlay position
  React.useEffect(() => {
    if (!selectedCell || !selectedCellOverlayRef.current) return;

    const cell = getCellRef(selectedCell.rowId, selectedCell.columnId);
    if (!cell.current) return;

    const cellRect = cell.current.getBoundingClientRect();
    const tableRect =
      selectedCellOverlayRef.current.parentElement?.getBoundingClientRect();

    // Get the scrolling position of the parent element
    const scrollLeft =
      selectedCellOverlayRef.current.parentElement?.scrollLeft || 0;
    const scrollTop =
      selectedCellOverlayRef.current.parentElement?.scrollTop || 0;

    const left =
      cellRect.left -
      (tableRect?.left ?? Number.POSITIVE_INFINITY) +
      scrollLeft;
    const top =
      cellRect.top - (tableRect?.top ?? Number.POSITIVE_INFINITY) + scrollTop;
    const width = cellRect.width;
    const height = cellRect.height;

    selectedCellOverlayRef.current.style.left = `${left}px`;
    selectedCellOverlayRef.current.style.top = `${top}px`;
    selectedCellOverlayRef.current.style.width = `${width}px`;
    selectedCellOverlayRef.current.style.height = `${height}px`;
  }, [selectedCell, getCellRef]);

  // Update range selection callback
  React.useEffect(() => {
    if (!onRangeSelectionChange) return;

    if (selectedRange) {
      onRangeSelectionChange({
        startCell: {
          rowId: selectedRange.start.rowId,
          columnId: selectedRange.start.columnId,
        },
        endCell: {
          rowId: selectedRange.end.rowId,
          columnId: selectedRange.end.columnId,
        },
      });
    } else {
      onRangeSelectionChange(null);
    }
  }, [selectedRange, onRangeSelectionChange]);

  React.useLayoutEffect(() => {
    domainColumns
      .filter((it) => it.pinned)
      .forEach((it) => it.pinned && table.getColumn(it.key)?.pin(it.pinned));
  }, [domainColumns, table]);

  // Handle column resize
  const handleColumnResize = React.useCallback(
    (columnId: string, width: number) => {
      onColumnResize?.(columnId, width);
    },
    [onColumnResize],
  );

  const calculateShowHShadow = (
    column: Column<DataGridRow<T>, unknown>,
  ): TableHeaderProps<T>['showHShadow'] => {
    if (!column.getIsPinned()) {
      return false;
    }

    if (column.getIsPinned() === 'left' && !maxHLScrolled) {
      return 'right';
    }

    if (column.getIsPinned() === 'right' && !maxHRScrolled) {
      return 'left';
    }

    return false;
  };

  React.useEffect(() => {
    if (isLoading) {
      rootRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isLoading]);

  if (hasError) {
    return (
      <div className="h-full">
        <Empty
          className="h-full"
          title={t.components.dataGrid.error}
          description={t.components.dataGrid.errorDescription}
          centered={true}
          icon={<LucideFrown className="size-24 stroke-[1px] text-failure" />}
          {...errorPanelProps}
        />
      </div>
    );
  }

  // Check if any column filters are active
  const hasActiveFilters = domainColumns.some((col) => col.isFilterActive);

  // Show loading state when loading and no data yet
  if (isLoading && dataSource.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        {loadingIndicator}
      </div>
    );
  }

  if (dataSource.length === 0 && !hasActiveFilters) {
    return (
      <div className="h-full">
        <Empty
          className="h-full"
          title={t.components.dataGrid.noData}
          centered={true}
          {...emptyPanelProps}
        />
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={cn(
        '@DataGrid relative',
        'min-h-[100px] w-full overflow-auto text-sm',
        isContained && 'rounded-lg border border-neutral/15 bg-space',
        stickyHeader &&
          '[&_thead]:sticky [&_thead]:top-0 [&_thead]:z-50 data-[scrolled=true]:[&_thead]:shadow-sm',
        className,
      )}
      data-type="data-grid"
      style={{
        height: scrollHeight,
        maxHeight: scrollHeight || '100%',
        overflowY: scrollHeight ? 'auto' : 'visible',
      }}
      data-scrolled={hasScrolled}
    >
      {/* Loading overlay using sticky positioning - stays centered in visible area */}
      {isLoading && (
        <div
          className="pointer-events-none absolute top-0 right-0 left-0 z-20 flex min-h-[300px] items-center justify-center"
          style={{
            height: Math.min(
              rootRef.current?.clientHeight ?? Number.MAX_SAFE_INTEGER,
              tableRef.current?.clientHeight ?? Number.MAX_SAFE_INTEGER,
            ),
          }}
          aria-hidden="true"
        >
          {loadingIndicator}
        </div>
      )}
      <div
        ref={selectionOverlayRef}
        className={cn(
          'pointer-events-none absolute',
          'z-10 border border-primary/20',
          !!selectedRange && 'opacity-100',
          !selectedRange && 'opacity-0',
        )}
      />
      <div
        ref={selectedCellOverlayRef}
        className={cn(
          'pointer-events-none absolute',
          'z-20 border-2 border-primary/60',
          !!selectedCell && 'opacity-100',
          !selectedCell && 'opacity-0',
        )}
      />
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <table
          ref={tableRef}
          className={cn(
            'border-separate border-spacing-0',
            (!fullWidth || table.getTotalSize() < (tableWidth ?? 1) - 1) &&
              'border-r border-neutral/10',
          )}
          style={{
            width: table.getTotalSize(),
            display: fullWidth && !tableWidth ? 'none' : undefined,
          }}
        >
          {showHeader && (
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  <SortableContext
                    items={columnOrder}
                    strategy={horizontalListSortingStrategy}
                  >
                    {headerGroup.headers.map((header) => (
                      <TableHeader
                        key={header.id}
                        header={header}
                        columnResizeDirection={
                          table.options.columnResizeDirection
                        }
                        columnResizeMode={table.options.columnResizeMode}
                        columnSortDirection={
                          columnSorting?.id === header.column.id
                            ? columnSorting.direction
                            : undefined
                        }
                        deltaOffset={
                          table.getState().columnSizingInfo.deltaOffset
                        }
                        onColumnResize={handleColumnResize}
                        onColumnSort={handleColumnSorting}
                        sticky={stickyHeaders}
                        showShadow={hasScrolled}
                        showHShadow={calculateShowHShadow(header.column)}
                        columnFilter={
                          (header.column.columnDef.meta as DataGridColumn<T>)
                            ?.columnFilter
                        }
                        isFilterActive={
                          (header.column.columnDef.meta as DataGridColumn<T>)
                            ?.isFilterActive
                        }
                      />
                    ))}
                  </SortableContext>
                </tr>
              ))}
            </thead>
          )}
          <tbody
            onClick={handleTableEvent}
            onKeyDown={handleTableEvent}
            onMouseDown={handleTableEvent}
            onMouseEnter={handleTableEvent}
            onMouseMove={handleTableEvent}
            onCopy={handleCopy}
          >
            {(() => {
              const rows = table.getRowModel().rows;

              // Original non-grouped rendering
              return rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const cellRef = getCellRef(cell.row.id, cell.column.id);
                    const isInRange = isCellInRange(
                      cell.row.id,
                      cell.column.id,
                    );

                    return (
                      <SortableContext
                        key={cell.id}
                        items={columnOrder}
                        strategy={horizontalListSortingStrategy}
                      >
                        <TableCell
                          key={cell.id}
                          ref={cellRef}
                          tabIndex={0}
                          data-row-id={cell.row.id}
                          data-column-id={cell.column.id}
                          cell={cell}
                          isInRange={isInRange}
                          isGroupRow={isGroupRow(cell.row.original)}
                          showHShadow={calculateShowHShadow(cell.column)}
                        />
                      </SortableContext>
                    );
                  })}
                </tr>
              ));
            })()}
          </tbody>
        </table>
      </DndContext>
    </div>
  );
};

export type ContainedDataGridsProps = { children: React.ReactNode };

export const ContainedDataGrids = ({ children }: ContainedDataGridsProps) => (
  <div
    className={cn(
      '@ContainedDataGrids',
      'contents **:data-[type="data-grid"]:rounded-lg **:data-[type="data-grid"]:border **:data-[type="data-grid"]:border-neutral/15',
    )}
  >
    {children}
  </div>
);
